import { animate, state, style, transition, trigger } from '@angular/animations';
import { HttpClient } from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  ComponentRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import interact from 'interactjs';
import { AwallComponentEvent } from '../../models/awallComponentEvent';
import { ComponentEvent } from '../../models/enums/componentEvent';
import { CommunicationService, Listener } from '../../services/communication.service';
import { DynamicElementService } from '../../services/dynamicElement.service';
import { WidgetDock } from '../../util/WidgetDock';
import { WidgetType } from '../../util/WidgetType';
import { DraggedWidgetHolder } from '../widget-wrapper/DraggedWidgetHolder';
import { WidgetWrapperComponent } from '../widget-wrapper/widget-wrapper.component';
import { WidgetWrapperState } from '../widget-wrapper/widgetWrapperState';
import { AppWidgetComponent } from '../widgets/app-widget/app-widget.component';
import { CopilotWidgetComponent } from '../widgets/copilot-widget/copilot-widget.component';
import { GalleryWidgetComponent } from '../widgets/gallery-widget/gallery-widget.component';
import { PiWidgetComponent } from '../widgets/pi-widget/pi-widget.component';
import { ProjectWidgetComponent } from '../widgets/project-widget/project-widget.component';
import { ReleaseplanningWidgetComponent } from '../widgets/releaseplanning-widget/releaseplanning-widget.component';
import { SprintinfoWidgetComponent } from '../widgets/sprintinfo-widget/sprintinfo-widget.component';
import { SprintplanningWidgetComponent } from '../widgets/sprintplanning-widget/sprintplanning-widget.component';
import { TeamMoodComponent } from '../widgets/team-mood/team-mood.component';
import { TeamWidgetComponent } from '../widgets/team-widget/team-widget.component';
import { InformationViewWidgetDock } from './InformationViewWidgetDock';
import { SpacerHelper } from './spacerHelper';
import { SettingsService } from '../../services/settings.service';
import { UserstoryService } from '../../services/userstory.service';

@Component({
  selector: 'app-informationview',
  templateUrl: './informationview.component.html',
  styleUrls: ['./informationview.component.scss'],
  animations: [
  trigger('widgetMenu', [
    state('closed', style({
      maxHeight: 0,
      paddingBottom: 0,
      })),
    state('open', style({
      maxHeight: '300px',
      paddingBottom: '14px',
      })),
    transition('closed => open', [
      animate('0.3s')
      ]),
    transition('open => closed', [
      animate('0.3s')
      ])
    ])
  ]
  })
export class InformationviewComponent implements OnInit, AfterViewInit, OnDestroy, Listener<AwallComponentEvent>  {
  @Input() private viewName: string;
  @ViewChild('widgetWrapperInsertionPoint', { read: ViewContainerRef, static: true }) widgetWrapperInsertionPoint: ViewContainerRef;

  public static readonly WIDGET_CONTAINER_ID = 'widgetcontainer';
  public widgetMenuIsOpen = false;
  public widgetTypes: WidgetType[];
  private readonly defaultLocalStorageValue = { active: false, index: 100 };
  public backgroundVideoIsActive = false;
  // This map contains the Componentref for each WidgetWrapper with the key of the corresponding widget-name
  public widgetWrapperComponentRefs = new Map<string, ComponentRef<WidgetWrapperComponent>>();
  public static readonly componentId = 'InformationviewComponent';

  private widgetDock: WidgetDock;

  constructor(private http: HttpClient,
    private communicationService: CommunicationService,
    private dynamicElementService: DynamicElementService,
    private viewContainerRef: ViewContainerRef,
    private settingsService: SettingsService,
    private userstoryService: UserstoryService
  ) {
    this.widgetTypes = this.initWidgetTypes();
    this.loadAndApplyConfig();
  }

  ngOnInit(): void {
    this.communicationService.registerComponent(InformationviewComponent.componentId, this);
  }

  ngAfterViewInit(): void {
    this.setupWidgetDock();
    this.setupActionviewWidgetDropzone();
    this.setupWidgetWrapperDropzones();
  }

  ngOnDestroy(): void {
    this.communicationService.unregisterComponent(InformationviewComponent.componentId);
    interact('.' + SpacerHelper.SPACER_DROPZONE_CSS_CLASSNAME).unset();
    interact('#actionview').unset();

    this.widgetDock.clearAll(this.widgetTypes);
  }

  public initWidgetTypes(): WidgetType[] {
    return [
      new WidgetType(ProjectWidgetComponent, ProjectWidgetComponent.widgetName, false, 100),
      new WidgetType(SprintinfoWidgetComponent, SprintinfoWidgetComponent.widgetName, false, 100),
      new WidgetType(TeamWidgetComponent, TeamWidgetComponent.widgetName, false, 100),
      new WidgetType(ReleaseplanningWidgetComponent, ReleaseplanningWidgetComponent.widgetName, false, 100),
      new WidgetType(SprintplanningWidgetComponent, SprintplanningWidgetComponent.widgetName, false, 100),
      new WidgetType(PiWidgetComponent, PiWidgetComponent.widgetName, false, 100),
      new WidgetType(GalleryWidgetComponent, GalleryWidgetComponent.widgetName, false, 100),
      new WidgetType(AppWidgetComponent, AppWidgetComponent.widgetName, false, 100),
      new WidgetType(CopilotWidgetComponent, CopilotWidgetComponent.widgetName, false, 100),

      // new WidgetType(BurndownWidgetComponent, BurndownWidgetComponent.widgetName, false, 100),
      // new WidgetType(DefinitionOfDoneWidgetComponent, DefinitionOfDoneWidgetComponent.widgetName, false, 100),
      new WidgetType(TeamMoodComponent, TeamMoodComponent.widgetName, false, 100),
    ];
  }

  public toggleWidgetMenu(event: any) {
    this.widgetMenuIsOpen = !this.widgetMenuIsOpen;
  }

  /**
  * Load the default-config for which widgets to display for the currently active view.
  * If a custom-settings is present in localStorage use this over default-setting.
  */
  private loadAndApplyConfig() {
    this.http.get('./assets/defaults/informationview-default-config.json')
      .subscribe(informationViewConfig => {
        this.widgetTypes.forEach(widgetType => {

          console.log('looking for defaults for view: ', this.viewName);

          let defaultActivitySetting = false;
          if (informationViewConfig.hasOwnProperty(this.viewName)) {
            defaultActivitySetting = informationViewConfig[this.viewName][widgetType.name];
          }

          const customSetting = this.loadWidgetSettingsFromLocalstorage(widgetType.name, false);

          if (customSetting) {
            widgetType.active = customSetting.active;
            widgetType.index = customSetting.index;
          } else {
            widgetType.active = defaultActivitySetting;
            // The default-settings doesnt have a index-value. We should evaluate if we should add this.
          }
        });
        this.sortWidgetTypes();
        this.dynamicallyAddWidgets();
        this.addLastSpacer();
      });
  }

  private dynamicallyAddWidgets() {
    for (const widgetType of this.widgetTypes) {
      if (widgetType.active) {
        this.widgetDock.activateWidget(widgetType);
      }
    }
  }

  onCommunicationEvent(event: AwallComponentEvent): void {
    if (!event) { return; }

    switch (event.name) {
      /* VTR-Widget sends us an event when a video goes to fullscreen so we can make the background transparent */
      case ComponentEvent.WIDGET_VTR_VIDEO_ACTIVE: {
        this.backgroundVideoIsActive = <boolean>event.value;
        break;
      }
      case ComponentEvent.INFOVIEW_INSERT_WRAPPER: {
        const widgetName = <string>event.value['widgetName'];
        const wrapperComponentRef = <ComponentRef<WidgetWrapperComponent>>event.value['wrapperComponentRef'];
        this.widgetDock.attachWidget(widgetName, wrapperComponentRef);
        break;
      }
      case ComponentEvent.INFOVIEW_REMOVE_WRAPPER: {
        const widgetName = <string>event.value['widgetName'];
        this.widgetDock.detachWidget(widgetName);
        break;
      }
      case ComponentEvent.INFOVIEW_UPDATE_SPACERS: {
        this.updateSpacers();
        break;
      }
    }
  }

  /**
   * Sort widgetTypes according to their index.
   */
  private sortWidgetTypes() {
    const defaultIndex = 100;
    this.widgetTypes.sort(function (a, b) {
      const indexA = (a) ? a.index : defaultIndex;
      const indexB = (b) ? b.index : defaultIndex;
      return indexA - indexB;
    });
  }

  private getWidgetTypeByName(widgetName: string) {
    return this.widgetTypes.find(widgetType => widgetType.name === widgetName);
  }

  onWidgetActivityChanged(widgetName: string, event: any) {
    const widgetType = this.getWidgetTypeByName(widgetName);

    if (widgetType) {
      widgetType.active = event.target.checked;

      if (widgetType.active) {
        this.widgetDock.activateWidget(widgetType);
      }
      else {
        this.widgetDock.deactivateWidget(widgetType);
      }

      this.saveWidgetSettingsToLocalstorage(widgetName, event.target.checked);
      this.setLastSpacerDropzoneSize();
    }
  }

  /**
   * Save widget status and/or index to localstorage. Uses default-values where no other value are present.
   */
  private saveWidgetSettingsToLocalstorage(widgetName: string, widgetActive?: boolean, widgetIndex?: number) {
    let value = this.loadWidgetSettingsFromLocalstorage(widgetName);
    value = (value) ? value : this.defaultLocalStorageValue;

    if (typeof widgetActive === 'boolean') {
      value.active = widgetActive;
    }
    if (typeof widgetIndex === 'number') {
      value.index = widgetIndex;
    }
    localStorage.setItem(this.viewName + '-widget-' + widgetName, JSON.stringify(value));
  }

  private loadWidgetSettingsFromLocalstorage(widgetName: string, getDefaultIfNoValue?: boolean) {
    const storedValue = localStorage.getItem(this.viewName + '-widget-' + widgetName);
    if (storedValue || getDefaultIfNoValue === false) {
      return JSON.parse(storedValue);
    } else {
      return this.defaultLocalStorageValue;
    }
  }

  private setupWidgetDock() {
    this.widgetDock =  new InformationViewWidgetDock(
      this.widgetWrapperInsertionPoint,
      this.dynamicElementService,
    );
  }

  /**
   * This sets up the actionview as dropzone for widgetwrappers
   */
  private setupActionviewWidgetDropzone() {
    interact('#actionview')
      .dropzone({
        accept: '.interact-widget > div.widget__inner > div.widget__header',
        overlap: 0.1,
        ondragenter: () => {
          const draggedWidgetWrapper = DraggedWidgetHolder.draggedWidget;
          if (!draggedWidgetWrapper.isDocked()) {
            return;
          }
          // Set State to 'Undocked Dragging' now that we entered the actionview
          draggedWidgetWrapper.setState(WidgetWrapperState.UNDOCKED_DRAGGING);
        }
      });
  }

  /**
   * This sets up dropzones in the infoview to drop (drock) widgetwrappers
   */
  private setupWidgetWrapperDropzones() {
    interact('.' + SpacerHelper.SPACER_DROPZONE_CSS_CLASSNAME)
      .dropzone({
        accept: '.interact-widget > div.widget__inner > div.widget__header',
        overlap: 'pointer',
        ondragenter: event => {
          const draggedWidgetWrapper = DraggedWidgetHolder.draggedWidget;
          // Set Spacer Layout as active (only for docked widgets now, see comment below)
          if (draggedWidgetWrapper.isDocked()) {
            const spacer = event.target.parentElement;
            spacer.classList.add(SpacerHelper.SPACER_ACTIVE_CSS_CLASSNAME);
          }
          // Disabled to test new concept proposed by M. Kropp (08/21): Widgets shouldnt be dragged back
          // and can only be docked by clicking the 'close'-button on the widget.
          /*
          const spacer = event.target.parentElement;
          spacer.classList.add(SpacerHelper.SPACER_ACTIVE_CSS_CLASSNAME);
          if (!draggedWidgetWrapper.isDocked()) {
            const mousePositionX = event.dragEvent.client.x;
            draggedWidgetWrapper.setState(WidgetWrapperState.DOCKED_DRAGGING, mousePositionX);
          }
          */
        },
        ondragleave: event => {
          const spacer = event.target.parentElement;
          spacer.classList.remove(SpacerHelper.SPACER_ACTIVE_CSS_CLASSNAME);
        },
        ondropdeactivate: event => {
          const spacer = event.target.parentElement;
          spacer.classList.remove(SpacerHelper.SPACER_ACTIVE_CSS_CLASSNAME);
        },
        ondrop: event => {
          this.onWidgetWrapperDropped(event);
        }
      });
  }

  /**
   * A WidgetWrapperComponent has been dropped into the informationview.
   * Move it to informationview's widget-container, remove placeholder, manage spacers
   */
  onWidgetWrapperDropped(event): void {
    // We need the grand-grand-parent of the dragged header to get the widgetWrapper-Element
    const widgetWrapper = event.relatedTarget.parentElement.parentElement.parentElement;
    // The spacer onto whos dropzone the widgetWrapper was dropped
    const targetedSpacer = event.target.parentElement;

    // If the wrapper was dropped in the same place it already was we do nothing
    if (!this.dropChangesWidgetOrder(targetedSpacer, widgetWrapper)) {
      return;
    }

    // If the wrapper is in the dynamicElementContainer and we de-register it there
    // and remove the 'position = absolute' before we move it back to the infoview container a few lines further down
    const container = InformationviewComponent.getWidgetContainer();
    if (!container.contains(widgetWrapper)) {
      const widgetName = this.getWidgetName(widgetWrapper);
      const wrapperComponentRef = DraggedWidgetHolder.draggedWidget.wrapperComponentRef;

      this.dynamicElementService.remove(wrapperComponentRef);
      this.widgetDock.attachWidget(widgetName, wrapperComponentRef);
      wrapperComponentRef.location.nativeElement.style.position = '';
    }

    // insert wrapper to the left of the spacer it was droppen onto
    container.insertBefore(widgetWrapper, targetedSpacer);
    // save the changed widget-order to localstorage
    this.saveWidgetOrder();
  }

  /**
   * Adds or removes spacers after elements (widgetWrappers, placeholders)have been added or removed to the
   * informationview to restore the order of each element having one wrapper to its left
   */
  private updateSpacers() {
    const container = InformationviewComponent.getWidgetContainer();
    let lastChildWasSpacer = false;
    const spacerToRemove: HTMLElement[] = [];
    const spacerToAddBefore: HTMLElement[] = [];

    Array.from(container.children).forEach((containerChild) => {
      const isSpacer = containerChild.classList.contains(SpacerHelper.SPACER_CSS_CLASSNAME);
      const isLastSpacer = containerChild.classList.contains(SpacerHelper.SPACER_LAST_CSS_CLASSNAME);

      // If there are Two spacers next to eachother, remove the left one
      if (!isLastSpacer && isSpacer && lastChildWasSpacer) {
        spacerToRemove.push(<HTMLElement>(containerChild.previousElementSibling));
      }
      // If there is a spacer on the left of last-spacer: remove it
      else if (isLastSpacer && lastChildWasSpacer) {
        spacerToRemove.push(<HTMLElement>(containerChild.previousElementSibling));
      }

      if (!isSpacer && !lastChildWasSpacer) {
        spacerToAddBefore.push(<HTMLElement>containerChild);
      }
      lastChildWasSpacer = isSpacer;
    });

    spacerToRemove.forEach((spacer) => {
      container.removeChild(spacer);
    });

    spacerToAddBefore.forEach((child) => {
      container.insertBefore(SpacerHelper.getNewSpacerElement(), child);
    });
  }

  /**
   * Save the order of the widgets in the informationview to localstorage
   */
  private saveWidgetOrder() {
    const container = InformationviewComponent.getWidgetContainer();
    const widgetWrapperElements: Array<HTMLElement> = Array.from(container.querySelectorAll('app-widget-wrapper'));
    widgetWrapperElements.forEach((widgetWrapperElement, index) => {
      const widgetName = this.getWidgetName(widgetWrapperElement);
      this.saveWidgetSettingsToLocalstorage(widgetName, undefined, index);
    });
  }

  private addLastSpacer() {
    const spacer = SpacerHelper.getNewSpacerElement();
    spacer.classList.add(SpacerHelper.SPACER_LAST_CSS_CLASSNAME);
    InformationviewComponent.getWidgetContainer().appendChild(spacer);
    this.setLastSpacerDropzoneSize();
  }

  // Set the dropzone (defined by the spacers only child element) to the correct size
  // TODO this might need to be called on additional events that change the space in the widgetcontainer
  private setLastSpacerDropzoneSize() {
    const container = InformationviewComponent.getWidgetContainer();
    const lastSpacer: HTMLElement = container.querySelector('.' + SpacerHelper.SPACER_LAST_CSS_CLASSNAME);
    if (lastSpacer) {
      const OFFSET = 55;
      const spacerPositionRight = window.innerWidth - lastSpacer.getBoundingClientRect().left;
      const lastDropZoneRightValue = (-1) * (spacerPositionRight - OFFSET);
      lastSpacer.firstElementChild.setAttribute('style', `right: ${lastDropZoneRightValue}px`);
    }
  }

  /**
   * This method tells us if the order of widgets in the widgetcontainer needs to be changed after dropping
   * widgetWrapper onto the dropzone of targetedSpacer.
   */
  private dropChangesWidgetOrder(targetedSpacer: HTMLElement, widgetWrapper: HTMLElement): boolean {
    return (targetedSpacer !== widgetWrapper.previousElementSibling && targetedSpacer !== widgetWrapper.nextElementSibling);
  }

  /**
   * For a widgetWrapper-HTMLelement return the name of the associated widget
   */
  private getWidgetName(widgetWrapperElement: HTMLElement) {
    const widgetName: string = widgetWrapperElement.querySelector('.widget').getAttribute('data-widget-name');
    return widgetName;
  }

  public static getWidgetContainer(): HTMLElement {
    return document.getElementById(InformationviewComponent.WIDGET_CONTAINER_ID);
  }
}
